/**
 * ZOUR-67 — the shapes the Agent Review API returns.
 *
 * Kept deliberately close to the backend payload rather than re-modelled: the
 * page is a reader, and every rename between here and
 * `AgentReviewController` is somewhere a field can go missing silently.
 */

/** The four statuses. `AgentReviewController` decides them; nothing here does. */
export type AgentReviewStatus =
  | "ready"
  | "needs_attention"
  | "checking"
  | "not_checked";

export type FindingGroup = "document_mismatch" | "booking_advisory";

export type FindingSeverity = "high" | "medium" | "low";

/** null = nobody has ruled on it yet. */
export type FindingDisposition = "not_an_error" | "confirmed" | null;

export interface AgentReviewListItem {
  bill_id: number;
  jobid: string | null;
  bill_number: string | null;
  vendor_id: string | null;
  vendor_name: string | null;
  date: string | null;
  total: string | null;
  status: AgentReviewStatus;
  run_id: number | null;
  verdict: string | null;
  summary: string | null;
  error: string | null;
  /** Blocking validation keys that failed — why a bill is not Ready. */
  blocking_failures: string[];
  /** Whether the agent could read the PDF. null when there is no run yet. */
  pdf_available: boolean | null;
  /** Why it could not, when that is known. */
  pdf_error: string | null;
  /** Already in Zoho. Its JSON is the corrected version, not what the agent read. */
  is_posted: boolean;
  /** What asked for the run. `backtest` means a measurement, not the desk's work. */
  trigger_type: "cron" | "manual" | "rerun" | "backtest" | null;
  mismatch_count: number;
  advisory_count: number;
  checked_at: string | null;
  /** The bill moved since its last run, so what is shown is out of date. */
  stale: boolean;
}

export interface AgentReviewListResponse {
  items: AgentReviewListItem[];
  pagination: {
    page: number;
    per_page: number;
    total: number;
    pages: number;
  };
  counts: Record<AgentReviewStatus, number>;
  /** Everything the non-status filters matched — what "All" counts. */
  total_shown?: number;
  /** false when the tenant has not run the agent_review_* migrations yet. */
  available?: boolean;
}

export interface AgentReviewFinding {
  id: number;
  run_id: number;
  bill_id: number;
  finding_group: FindingGroup;
  field: string;
  pdf_value: string | null;
  our_value: string | null;
  severity: FindingSeverity;
  reason: string | null;
  /** Quoted from the PDF or from the history row. Never empty — a guardrail rejects that. */
  evidence: string | null;
  page: number | null;
  disposition: FindingDisposition;
  disposition_by: number | null;
  disposition_at: string | null;
}

export interface AgentReviewRun {
  id: number;
  bill_id: number;
  jobid: string | null;
  input_hash: string;
  hash_version: number;
  trigger_type: "cron" | "manual" | "rerun" | "backtest";
  model: string;
  prompt_version: string;
  /** The model's own word. The page shows `is_ready`, which ReadyPolicy computes. */
  verdict: string | null;
  is_ready: number | null;
  blocking_failures: string[];
  pdf_available?: number | null;
  pdf_error?: string | null;
  summary: string | null;
  cannot_verify: string[];
  status: "queued" | "running" | "done" | "error";
  error: string | null;
  prompt_tokens: number;
  completion_tokens: number;
  cost_usd: string;
  started_at: string | null;
  finished_at: string | null;
}

export interface AgentReviewStep {
  id: number;
  run_id: number;
  seq: number;
  kind: "model_call" | "tool_call" | "guardrail";
  name: string;
  tokens: number;
  duration_ms: number;
}

export interface AgentReviewRunDetail {
  run: AgentReviewRun | null;
  findings: AgentReviewFinding[];
  steps: AgentReviewStep[];
  snapshot: {
    validation_results: Record<string, unknown> | null;
    diff: unknown[] | null;
    validated_json: Record<string, unknown> | null;
  } | null;
  /** Only present when a bill has never been reviewed. */
  status?: AgentReviewStatus;
}

export interface AgentReviewDashboard {
  window_days: number;
  /** Whether posted bills' runs are in these figures. */
  include_posted: boolean;
  /** How many of the counted bills are already in Zoho. */
  posted_bills_included: number;
  bills_checked: number;
  ready: number;
  needs_attention: number;
  /** Of those, the ones nobody could check against a document. */
  needs_attention_no_document: number;
  errored: number;
  bills_with_findings: number;
  share_with_findings: number | null;
  cost_usd: number;
  cost_per_bill: number;
  findings_by_group: Array<{
    finding_group: FindingGroup;
    severity: FindingSeverity;
    n: string;
  }>;
  findings_by_field: Array<{
    field: string;
    finding_group: FindingGroup;
    n: string;
    not_an_error: string | null;
  }>;
  findings_by_vendor: Array<{
    vendor_id: string | null;
    vendor_name: string | null;
    findings: string;
    bills: string;
  }>;
  dispositions: {
    total: number;
    not_an_error: number;
    confirmed: number;
    undecided: number;
    /** Of the findings someone has actually ruled on. null until one is. */
    false_alarm_rate: number | null;
  };
}

export interface AgentReviewFilters {
  status?: AgentReviewStatus | "";
  vendor_id?: string;
  from?: string;
  to?: string;
  search?: string;
  page?: number;
  per_page?: number;
  /** 1 = also list bills already posted to Zoho. Off by default. */
  include_posted?: 1 | "";
}

/** What each status means, in the words the page uses. */
export const STATUS_LABEL: Record<AgentReviewStatus, string> = {
  ready: "Ready",
  needs_attention: "Needs attention",
  checking: "Checking",
  not_checked: "Not checked",
};

export const STATUS_HELP: Record<AgentReviewStatus, string> = {
  ready:
    "Every blocking check passed and the agent found nothing that contradicts the document.",
  needs_attention:
    "A blocking check failed, the document could not be read, or the agent found something that does not match it.",
  checking:
    "Waiting for the background review, or the bill has changed since it was last looked at.",
  not_checked: "The last attempt failed. The reason is on the run.",
};
